const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/ogg",
  "audio/mp4",
];

export const VOICE_MAX_MS = 180_000;
const VOICE_MIN_MS = 400;

function audioMimeBase(mime: string): string {
  return mime.trim().toLowerCase().split(";")[0]!.trim();
}

export function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  for (const t of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return undefined;
}

export function isVoiceRecordingSupported(): boolean {
  return Boolean(
    typeof navigator !== "undefined" &&
      navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== "undefined"
  );
}

function extForRecorderMime(mime: string): string {
  const base = audioMimeBase(mime);
  if (base === "audio/ogg") return ".ogg";
  if (base === "audio/mp4") return ".m4a";
  return ".webm";
}

function voiceFilename(mime: string): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}`;
  return `Голосовое_${stamp}${extForRecorderMime(mime)}`;
}

function stopStreamTracks(stream: MediaStream) {
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      /* ignore */
    }
  }
}

export type VoiceRecordingSession = {
  mimeType: string;
  stop: () => Promise<File>;
  cancel: () => void;
};

function processVoiceStream(mic: MediaStream): { stream: MediaStream; dispose: () => void } {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return { stream: mic, dispose: () => undefined };
    const ctx = new Ctx();
    const source = ctx.createMediaStreamSource(mic);
    // Небольшой запас по уровню + компрессор пиков: громкая речь не клиппится,
    // тихая не пропадает. AGC браузера выключен отдельно.
    const pre = ctx.createGain();
    pre.gain.value = 0.82;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -12;
    comp.knee.value = 16;
    comp.ratio.value = 4;
    comp.attack.value = 0.003;
    comp.release.value = 0.18;
    const dest = ctx.createMediaStreamDestination();
    source.connect(pre);
    pre.connect(comp);
    comp.connect(dest);
    void ctx.resume();
    return {
      stream: dest.stream,
      dispose: () => {
        try {
          source.disconnect();
          pre.disconnect();
          comp.disconnect();
        } catch {
          /* ignore */
        }
        void ctx.close();
      },
    };
  } catch {
    return { stream: mic, dispose: () => undefined };
  }
}

export async function startVoiceRecording(opts?: {
  maxMs?: number;
}): Promise<VoiceRecordingSession> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Микрофон недоступен в этом браузере");
  }
  if (typeof MediaRecorder === "undefined") {
    throw new Error("Запись аудио не поддерживается");
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: false,
      channelCount: 1,
    } as MediaTrackConstraints,
  });
  const micTrack = stream.getAudioTracks()[0];
  if (micTrack) {
    try {
      await micTrack.applyConstraints({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false,
      });
    } catch {
      /* браузер не дал отключить AGC — режем уровень в processVoiceStream */
    }
  }

  const processed = processVoiceStream(stream);
  const recordStream = processed.stream;

  const preferred = pickRecorderMimeType();
  let recorder: MediaRecorder;
  try {
    recorder = preferred
      ? new MediaRecorder(recordStream, { mimeType: preferred, audioBitsPerSecond: 96_000 })
      : new MediaRecorder(recordStream, { audioBitsPerSecond: 96_000 });
  } catch {
    recorder = preferred
      ? new MediaRecorder(recordStream, { mimeType: preferred })
      : new MediaRecorder(recordStream);
  }
  const mimeType = audioMimeBase(recorder.mimeType || preferred || "audio/webm");
  const chunks: BlobPart[] = [];
  let settled = false;
  let discard = false;
  let maxTimer: ReturnType<typeof setTimeout> | null = null;
  const startedAt = Date.now();

  let resolveStop: ((file: File) => void) | null = null;
  let rejectStop: ((err: Error) => void) | null = null;
  const stopPromise = new Promise<File>((resolve, reject) => {
    resolveStop = resolve;
    rejectStop = reject;
  });

  function finish(err?: Error, file?: File) {
    if (settled) return;
    settled = true;
    if (maxTimer) clearTimeout(maxTimer);
    maxTimer = null;
    stopStreamTracks(stream);
    processed.dispose();
    if (err) rejectStop?.(err);
    else if (file) resolveStop?.(file);
    else rejectStop?.(new Error("Запись отменена"));
  }

  recorder.ondataavailable = (ev) => {
    if (ev.data && ev.data.size > 0) chunks.push(ev.data);
  };

  recorder.onerror = () => {
    finish(new Error("Ошибка записи микрофона"));
  };

  recorder.onstop = () => {
    if (settled) return;
    if (discard) {
      finish(new Error("Запись отменена"));
      return;
    }
    const elapsed = Date.now() - startedAt;
    if (elapsed < VOICE_MIN_MS || chunks.length === 0) {
      finish(new Error("Слишком короткая запись"));
      return;
    }
    const blob = new Blob(chunks, { type: mimeType });
    if (blob.size < 64) {
      finish(new Error("Пустая запись"));
      return;
    }
    finish(undefined, new File([blob], voiceFilename(mimeType), { type: mimeType }));
  };

  recorder.start(250);

  const maxMs = opts?.maxMs ?? VOICE_MAX_MS;
  maxTimer = setTimeout(() => {
    if (recorder.state === "recording") recorder.stop();
  }, maxMs);

  return {
    mimeType,
    stop: () => {
      if (settled) return stopPromise;
      if (recorder.state === "inactive") {
        finish(new Error("Запись уже остановлена"));
        return stopPromise;
      }
      try {
        recorder.stop();
      } catch (e) {
        finish(e instanceof Error ? e : new Error("Не удалось остановить запись"));
      }
      return stopPromise;
    },
    cancel: () => {
      discard = true;
      if (recorder.state === "inactive") {
        finish(new Error("Запись отменена"));
        return;
      }
      try {
        recorder.stop();
      } catch {
        finish(new Error("Запись отменена"));
      }
    },
  };
}
